<template>
  <div
    ref="modalRoot"
    class="test-studio-reviews-modal"
    tabindex="-1"
    v-on:keyup.esc="$store.commit('clearCurrentModal')"
  >
    <mdb-modal centered elegant>
      <mdb-modal-header :close="false">
        <mdb-modal-title class="isi-heading-blue">
          Reviews for {{ options.testStudioName }}
        </mdb-modal-title>
      </mdb-modal-header>
      <mdb-modal-body>
        <!-- New Review Button -->
        <div class="mb-3 text-center">
          <mdb-btn color="isi" class="px-4" @click="handleNewReviewClick">
            <mdb-icon icon="plus" class="mr-2" />
            Click here to write a review
          </mdb-btn>
        </div>
        <div v-if="user.role === 3 || pendingReviews.length > 0" class="review-section mb-4">
          <h5>Pending ({{ pendingReviews.length }})</h5>
          <ul class="list-group mb-2">
          <review-list-item
            v-for="r in pendingReviews"
            :key="r.id"
            :review="r"
            @move-up="handleMoveUpClick"
            @move-down="handleMoveDownClick"
            @edit="handleEditReviewClick"
            @approve="handleApproveReviewClick"
            @reject="handleRejectReviewClick"
            @delete="handleDeleteReviewClick"
          />
            <li v-if="pendingReviews.length === 0" class="list-group-item text-muted">
              No pending reviews.
            </li>
          </ul>
        </div>
        <div class="review-section mb-4">
          <h5>{{ user.role === 3 ? 'Approved' : 'Reviews' }} ({{ approvedReviews.length }})</h5>
          <ul class="list-group mb-2">
            <review-list-item
              v-for="r in approvedReviews"
              :key="r.id"
              :review="r"
              @move-up="handleMoveUpClick"
              @move-down="handleMoveDownClick"
              @edit="handleEditReviewClick"
              @approve="handleApproveReviewClick"
              @reject="handleRejectReviewClick"
              @delete="handleDeleteReviewClick"
            />
            <li v-if="approvedReviews.length === 0" class="list-group-item text-muted">
              No reviews.
            </li>
          </ul>
        </div>
        <div v-if="user.role === 3 || deniedReviews.length > 0" class="review-section">
          <h5>Denied ({{ deniedReviews.length }})</h5>
          <ul class="list-group">
            <review-list-item
              v-for="r in deniedReviews"
              :key="r.id"
              :review="r"
              @move-up="handleMoveUpClick"
              @move-down="handleMoveDownClick"
              @edit="handleEditReviewClick"
              @approve="handleApproveReviewClick"
              @reject="handleRejectReviewClick"
              @delete="handleDeleteReviewClick"
            />
            <li v-if="deniedReviews.length === 0" class="list-group-item text-muted">
              No denied reviews.
            </li>
          </ul>
        </div>
      </mdb-modal-body>
      <mdb-modal-footer>
        <mdb-btn class="btn btn-isi" @click.native="$store.commit('clearCurrentModal')">
          Close
        </mdb-btn>
      </mdb-modal-footer>
    </mdb-modal>
    <!-- New Review Dialog -->
    <review-editor-modal
      :visible="editDialog"
      :review="newReview"
      @cancel="handleCancelReviewEditorClick"
      @submit="handleSubmitReviewClick" />
    <!-- Delete Confirmation Dialog -->
  <delete-review-confirmation-modal
    :visible="deleteDialog"
    :review="reviewToDelete"
    @cancel="cancelDelete"
    @confirm="confirmDelete"
  />
  </div>
</template>

<script>
import ReviewListItem from '@/components/ReviewListItem.vue'
import DeleteReviewConfirmationModal from '@/components/Modals/DeleteReviewConfirmationModal.vue'
import ReviewEditorModal from '@/components/Modals/ReviewEditorModal.vue'
export default {
  props: {
    options: {
      type: Object,
      required: true
    }
  },
  components: { ReviewListItem, ReviewEditorModal, DeleteReviewConfirmationModal },
  mounted() {
    document.body.style.overflow = 'hidden';
    this.$nextTick(() => {
      this.$refs.modalRoot?.focus({ preventScroll: true });
    });
  },
  beforeDestroy() {
    document.body.style.overflow = '';
  },
  data() {
    return {
      editDialog: false,
      newReview: {
        title: '',
        message: '',
        projectNumber: '',
        projectName: ''
      },
      // Confirmation state for deletion
      deleteDialog: false,
      reviewToDelete: null,
    };
  },
  methods: {
    handleNewReviewClick() {
      this.editDialog = true;
      this.newReview = {
        title: '',
        message: '',
        projectNumber: '',
        projectName: ''
      };
    },
    handleCancelReviewEditorClick() {
      this.editDialog = false;
    },
    async handleSubmitReviewClick(submittedReview) {
      const payload = { ...submittedReview, testStudioId: this.options.testStudioId };
      try {
        if (payload.id) {
          // Update existing review
          await this.$http.put('/api/TestStudioReview/update', payload);
        } else {
          // Create new review
          await this.$http.post('/api/TestStudioReview/create', payload);
        }
        // Reload backend data so computed reviews auto-update
        await this.$store.dispatch('getBackendData', this.$http);
      } catch (error) {
        console.error('Error saving review:', error);
      } finally {
        this.editDialog = false;
      }
    },
    handleEditReviewClick(review) {
      this.newReview = { ...review };
      this.editDialog = true;
    },
    async handleApproveReviewClick(review) {
      try {
        await this.$http.post('/api/TestStudioReview/complete', { id: review.id });
        await this.$store.dispatch('getBackendData', this.$http);
      } catch (e) {
        console.error('Error approving review:', e);
      }
    },
    async handleRejectReviewClick(review) {
      try {
        await this.$http.post('/api/TestStudioReview/reject', { id: review.id });
        await this.$store.dispatch('getBackendData', this.$http);
      } catch (e) {
        console.error('Error rejecting review:', e);
      }
    },
    // Opens confirmation dialog for deletion
    handleDeleteReviewClick(review) {
      this.reviewToDelete = review;
      this.deleteDialog = true;
    },
    // Confirm deletion
    async confirmDelete() {
      try {
        await this.$http.delete('/api/TestStudioReview/Delete', { data: { id: this.reviewToDelete.id } });
        await this.$store.dispatch('getBackendData', this.$http);
      } catch (e) {
        console.error('Error deleting review:', e);
      } finally {
        this.deleteDialog = false;
        this.reviewToDelete = null;
      }
    },
    // Cancel deletion
    cancelDelete() {
      this.deleteDialog = false;
      this.reviewToDelete = null;
    },
    // Move review position up
    async handleMoveUpClick(review) {
      // Determine current index in approvedReviews array
      const idx = this.approvedReviews.findIndex(r => r.id === review.id);
      if (idx <= 0) return;
      const newPosition = idx - 1;
      try {
        await this.$http.post('/api/TestStudioReview/position', { id: review.id, newPosition });
        await this.$store.dispatch('getBackendData', this.$http);
      } catch (e) {
        console.error('Error moving review up:', e);
      }
    },
    // Move review position down
    async handleMoveDownClick(review) {
      // Determine current index in approvedReviews array
      const idx = this.approvedReviews.findIndex(r => r.id === review.id);
      if (idx < 0 || idx >= this.approvedReviews.length - 1) return;
      const newPosition = idx + 1;
      try {
        await this.$http.post('/api/TestStudioReview/position', { id: review.id, newPosition });
        await this.$store.dispatch('getBackendData', this.$http);
      } catch (e) {
        console.error('Error moving review down:', e);
      }
    }
  },
  computed: {
    user() {
      return this.$store.state.user;
    },
    reviews() {
      // Fetch raw reviews array from Vuex store for this testStudioId
      const studio = this.$store.state.testStudios.items.find(s => s.id === this.options.testStudioId);
      return studio && Array.isArray(studio.reviews) ? studio.reviews : [];
    },
    pendingReviews() {
      // Filter pending reviews and sort by updatedAt ascending
      return [...this.reviews]
        .filter(r => r.state === 0)
        .sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));
    },
    approvedReviews() {
      // Filter approved reviews and sort by position ascending
      return [...this.reviews]
        .filter(r => r.state === 1)
        .sort((a, b) => (a.position || 0) - (b.position || 0));
    },
    deniedReviews() {
      // Filter denied reviews and sort by updatedAt ascending
      return [...this.reviews]
        .filter(r => r.state === 2)
        .sort((a, b) => new Date(a.updatedAt) - new Date(b.updatedAt));
    }
  }
};
</script>

<style>
/* Modal layout: header/footer auto, body flex-grow with scrollable sections */
.test-studio-reviews-modal .modal-content {
  display: flex;
  flex-direction: column;
  max-height: 80vh;
}
/* Prevent background scroll on modal open */
.no-scroll {
  overflow: hidden;
}
 .test-studio-reviews-modal mdb-modal-body,
 .test-studio-reviews-modal .modal-body {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  /* prevent scroll chaining to background */
  overscroll-behavior: contain;
}
.test-studio-reviews-modal .review-section {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  margin-bottom: 1rem;
}
.test-studio-reviews-modal .review-section:last-child {
  margin-bottom: 0;
}
.test-studio-reviews-modal .review-section h5 {
  margin-bottom: 0.5rem;
  font-weight: 600;
  flex: 0 0 auto;
}
.test-studio-reviews-modal .review-section .list-group {
  flex: 1 1 auto;
  margin: 0.5rem 0;
  overflow-y: auto;
}
/* limit modal width to about half the viewport */
.test-studio-reviews-modal .modal-dialog {
  width: 40vw;
  max-width: 40vw;
}
</style>
